/* eslint-disable react/no-danger */
import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import DOMPurify from 'isomorphic-dompurify';
import { useWindowSize } from 'hooks';
import { GoogleReviews, TextAndImageBlock, SEO, Button, Testimonials, Video } from 'components';
import { Hero } from 'components/ProductCollectionPage/Shared';
import { getAltTagFromFileName } from 'utils';

function AboutUs({ data }) {
  const { windowWidth } = useWindowSize();
  const { heroData, personCleaningStairs, oakStairs, woodFilings, personWalkingUpStairs } = data;

  const { heroImage, heroTitle, heroDescription } = heroData.frontmatter;
  return (
    <>
      <Hero
        height="14.75rem"
        heroImage={heroImage}
        heroImageOffset={-162}
        filter="contrast(115%) saturate(160%)"
        gradientPercentage="80%"
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Newel Posts"
        breadcrumb="About Us">
        <h1
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              windowWidth < 640 ? heroTitle.replace('<br />', ' ') : heroTitle
            )
          }}
          style={{ fontSize: '2.325rem' }}
        />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <Grid $backgroundColor={windowWidth < 1200 ? 'var(--connolly-grey)' : '#fff'}>
        <TextAndImageBlock
          image={personCleaningStairs.childImageSharp.gatsbyImageData}
          imageTitle="About Us"
          imageAlt={getAltTagFromFileName(personCleaningStairs.name)}
          paddingTop={windowWidth < 1200 ? '39px' : '178px'}>
          <h3>About Us</h3>
          <p>
            From our Dublin Joinery our team of skilled artisans combines years of expertise with a
            keen eye for detail, ensuring every product is not only aesthetically pleasing but also
            built to last. From our initial call out and quotation, to the design and final
            installation, we guide you every step of the way, working closely with you to bring your
            vision to life, offering personalised solutions that reflect your unique style and meet
            your specific needs.
            <br />
            <br />
            Trust us to transform your space with elegance and precision, making every step in your
            home a beautiful journey.
          </p>
          <Button to="/contact-us/" style={{ width: '145px' }}>
            Contact Us
          </Button>
        </TextAndImageBlock>
        <TextAndImageBlock
          image={oakStairs.childImageSharp.gatsbyImageData}
          imageTitle="Oak Stairs"
          imageAlt={getAltTagFromFileName(oakStairs.name)}
          flip
          paddingTop={windowWidth < 1200 ? '39px' : '153px'}>
          <h3>Selecting the staircases and doors that are right for you</h3>
          <p>
            Our approach is customer-centric, always considering your budget while ensuring the end
            result aligns with your vision. Whether you&apos;re choose from one of our pre-existing
            designs or prefer a bespoke staircase tailored specifically for your home, we are here
            to guide you every step of the way.
            <br />
            <br />
            For staircases, choose from one of our pre-existing designs or we will design a bespoke
            staircase just for you.
            <br />
            <br />
            For doors, we have curated Irelands best selling doors collection, from glazed doors,
            shaker doors, to statement doors and everything in between, insuring you have the
            perfect choices for your home or project.
          </p>
          <Button
            to="/stairs/"
            style={{ width: '160px', marginRight: '20px', whiteSpace: 'nowrap' }}>
            View Staircases
          </Button>
          <Button to="/doors/" style={{ width: '140px', whiteSpace: 'nowrap' }}>
            View Doors
          </Button>
        </TextAndImageBlock>
        <H3>Watch Our Joinery At Work</H3>
        <Video url="https://player.vimeo.com/video/975089753?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;dnt=1" />
        <TextAndImageBlock
          image={woodFilings.childImageSharp.gatsbyImageData}
          imageTitle="Wood Filings"
          imageAlt={getAltTagFromFileName(woodFilings.name)}
          paddingTop={windowWidth < 1200 ? '39px' : '100px'}>
          <h3>Knowledge & Advice</h3>
          <p>
            At our Connolly Stairs and Doors, we have been perfecting our craft since 2002. Our
            culture is one of constant improvement and innovation. We&apos;d like to share our
            knowledge with you, so that you can find the perfect stair for your home in Ireland.
          </p>
          <Button to="/showcases/" style={{ width: '218px' }}>
            View Our Showcases
          </Button>
        </TextAndImageBlock>
        <TextAndImageBlock
          image={personWalkingUpStairs.childImageSharp.gatsbyImageData}
          imageTitle="Delivery"
          imageAlt={getAltTagFromFileName(personWalkingUpStairs.name)}
          flip
          paddingTop={windowWidth < 1200 ? '39px' : '100px'}>
          <h3>Delivery</h3>
          <p>
            We know how frustrating life can be when you&apos;re co-ordinating trades, trying to
            bring everything together at exactly the right time. That&apos;s why transparency and
            integrity are so high on our priority list. We stick to our deadlines and maintain a
            transparent relationship with our customers, so you can always rely on high quality
            stairs in Ireland, delivered on time across all 32 counties.
          </p>
          <Button to="/stairs/" style={{ width: '170px', whiteSpace: 'nowrap' }}>
            View Products
          </Button>
        </TextAndImageBlock>
      </Grid>
      <GoogleReviews className="fade-in" backgroundColor="#fff" />
      {/* <Testimonials backgroundColor="#fff" /> */}
    </>
  );
}

const H3 = styled.h3`
  font-size: 2rem;
  grid-column: 1 / -1;
  margin: 2.3em 0 1.275em;
  text-align: center;
`;

const Grid = styled.section`
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'var(--connolly-grey)'};
  column-gap: 20px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 100px));
  justify-content: center;
  padding: 0 1.25rem;

  @media (width >= 768px) {
    padding: 0 0.625rem;
  }
`;

export const query = graphql`
  query {
    heroData: markdownRemark(frontmatter: { title: { eq: "About Us" } }) {
      id
      frontmatter {
        heroTitle
        heroDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
    personCleaningStairs: file(relativePath: { eq: "about-us/person-cleaning-stairs.png" }) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 590, formats: [AUTO, WEBP, AVIF])
      }
    }
    oakStairs: file(relativePath: { eq: "about-us/oak-closed-string-closed-riser-stairs.png" }) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 590, formats: [AUTO, WEBP, AVIF])
      }
    }
    woodFilings: file(
      relativePath: { eq: "about-us/three-planks-of-wood-next-to-wood-filings.png" }
    ) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 300, formats: [AUTO, WEBP, AVIF])
      }
    }
    personWalkingUpStairs: file(
      relativePath: { eq: "about-us/person-walking-up-white-stairs.png" }
    ) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 437, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="About Connolly Stairs & Doors | Quality Craftsmanship Ireland"
      description="Learn more about Connolly Stairs & Doors, a trusted supplier in Ireland committed to quality, expertise, and excellent customer service.">
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Stairs Ireland", "item": "https://www.connollystairs.ie/" },
          { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.connollystairs.ie/about-us/" }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default AboutUs;
